import { useEffect, useMemo, useState } from 'react';
import { handleDetailFieldChange, handleFieldChange, handleThueFieldChange } from '../../../utils/calc-util';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../../utils';
import { formSchema, initialFormValues, type FormSchema } from '../../../schema';
import { useLoading, useSearchFieldStates } from '../../../hooks';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { usePhiNganHangRows } from './usePhiNganHangRows';
import { useAuth } from '@/contexts/auth-context';
import { useDetailRows } from './useDetailRows';
import type { FormDialogProps } from '../types';
import { useThueRows } from './useThueRows';

export const useFormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const { loading } = useLoading({
    isOpen: open,
    external: false,
    duration: 500
  });
  const detailRows = useDetailRows(initialData?.chi_tiet_data || [], undefined, handleDetailFieldChange);
  const taxRows = useThueRows(initialData?.thue_data || [], undefined, handleThueFieldChange);
  const bankFeeRows = usePhiNganHangRows(initialData?.phi_ngan_hang_data || [], undefined, handleFieldChange);
  const { state, actions } = useSearchFieldStates(initialData);
  const repeatDetailRows = useRepeatRowsHandler({
    setRows: detailRows.setRows,
    markDirty: () => setIsFormDirty(true)
  });
  const repeatTaxRows = useRepeatRowsHandler({
    setRows: taxRows.setRows,
    markDirty: () => setIsFormDirty(true)
  });
  const repeatBankFeeRows = useRepeatRowsHandler({
    setRows: bankFeeRows.setRows,
    markDirty: () => setIsFormDirty(true)
  });

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
      setActiveTab('info');
    }
  }, [open]);

  const totals = useMemo(
    () => calculateTotals(detailRows.rows, taxRows.rows, bankFeeRows.rows),
    [detailRows.rows, taxRows.rows, bankFeeRows.rows]
  );

  const handleSubmit = async (data: FormSchema) => {
    const formData = transformFormData(
      data,
      state,
      totals,
      entityUnit,
      detailRows.rows,
      taxRows.rows,
      bankFeeRows.rows,
      formMode
    );

    await onSubmit?.({ ...formData });
    setIsFormDirty(false);
  };

  const handleClose = () => {
    onClose();
  };

  return {
    formMode,
    initialData,
    initialValues: (initialData || initialFormValues) as Partial<FormSchema>,
    schema: formSchema,
    loading,
    activeTab,
    setActiveTab,
    title: getFormTitle(formMode, activeTab),
    actionButtons: getFormActionButtons(formMode, activeTab, {
      onAdd,
      onEdit,
      onDelete,
      onCopy,
      handleClose
    }),
    formState: {
      state,
      actions,
      isDirty: isFormDirty,
      markDirty: () => setIsFormDirty(true)
    },
    onClose: handleClose,
    submit: handleSubmit,
    totals,
    detail: {
      rows: detailRows.rows,
      tableProps: {
        rows: detailRows.rows,
        selectedRowUuid: detailRows.selectedRowUuid,
        selectedCell: detailRows.selectedCell,
        onRowClick: detailRows.handleRowClick,
        onCellClick: detailRows.handleCellClick,
        onAddRow: detailRows.handleAddRow,
        onDeleteRow: detailRows.handleDeleteRow,
        onCopyRow: detailRows.handleCopyRow,
        onPasteRow: detailRows.handlePasteRow,
        onMoveRow: detailRows.handleMoveRow,
        onCellValueChange: detailRows.handleCellValueChange,
        handleUpdateNonWritableFields: detailRows.setNonWritableFields,
        handleUpdatePasteConfigs: detailRows.setPasteConfigs,
        status: state.loaiChungTu,
        loaiHD: state.loai_hd,
        setNhomLoaiHD: actions.setNhomLoaiHD,
        isPasting: detailRows.isPasting,
        onRepeatForRows: repeatDetailRows
      }
    },
    tax: {
      rows: taxRows.rows,
      tableProps: {
        rows: taxRows.rows,
        selectedRowUuid: taxRows.selectedRowUuid,
        onRowClick: taxRows.handleRowClick,
        onAddRow: taxRows.handleAddRow,
        onDeleteRow: taxRows.handleDeleteRow,
        onCopyRow: taxRows.handleCopyRow,
        onPasteRow: taxRows.handlePasteRow,
        onMoveRow: taxRows.handleMoveRow,
        onCellValueChange: taxRows.handleCellValueChange,
        onRepeatForRows: repeatTaxRows
      }
    },
    bankFee: {
      rows: bankFeeRows.rows,
      tableProps: {
        rows: bankFeeRows.rows,
        selectedRowUuid: bankFeeRows.selectedRowUuid,
        selectedCell: bankFeeRows.selectedCell,
        onRowClick: bankFeeRows.handleRowClick,
        onCellClick: bankFeeRows.handleCellClick,
        onAddRow: bankFeeRows.handleAddRow,
        onDeleteRow: bankFeeRows.handleDeleteRow,
        onCopyRow: bankFeeRows.handleCopyRow,
        onPasteRow: bankFeeRows.handlePasteRow,
        onMoveRow: bankFeeRows.handleMoveRow,
        onCellValueChange: bankFeeRows.handleCellValueChange,
        handleUpdateNonWritableFields: bankFeeRows.setNonWritableFields,
        handleUpdatePasteConfigs: bankFeeRows.setPasteConfigs,
        onRepeatForRows: repeatBankFeeRows,
        isPasting: bankFeeRows.isPasting
      }
    }
  };
};
